<script setup lang="ts">
import { onMounted, ref } from 'vue'
import { RouterView, useRouter } from 'vue-router'
import { useConfigStore } from './stores/config'
import { useVehicleStore } from './stores/vehicle'
import { useAgreementStore } from './stores/agreement'
import { useIframeStore } from './stores/iframe'
import localStore from '@/stores/local'
import { storeToRefs } from 'pinia'
import { useMessageStore } from './stores/messageStore'
import { useHead } from '@unhead/vue'
import { checkHealth } from './api'

const configStore = useConfigStore()
const vehicleStore = useVehicleStore()
const agreementStore = useAgreementStore()
const iframeStore = useIframeStore()
const { messages } = storeToRefs(useMessageStore())
const router = useRouter()

const loading = ref(true)
const healthy = ref(true)

agreementStore.$subscribe((state) => {
  localStore.setItem('agreement', JSON.parse(JSON.stringify(agreementStore.agreement)))
})

vehicleStore.$subscribe(() => {
  if (vehicleStore.manualVehicle) {
    localStore.setItem('manualVehicle', JSON.parse(JSON.stringify(vehicleStore.manualVehicle)))
  }
})

onMounted(async () => {
  window.addEventListener('message', (event) => {
    if (event.data?.type === 'Calc_Call' && typeof event.data.agreementResponsible === 'string') {
      iframeStore.setFromMessage(event.data.agreementResponsible)
    }
  })

  try {
    healthy.value = await checkHealth()

    if (healthy.value) {
      const config = await configStore.getConfig()
      await agreementStore.getSavedState()
      await vehicleStore.getSaved()

      useHead({ title: 'SAM Calc' + (config?.name ? ` - ${config.name}` : '') })
    }
  } finally {
    loading.value = false
  }
})
</script>

<template>
  <v-fade-transition group mode="out-in" hide-on-leave>
    <v-container v-if="loading" class="h-screen d-flex flex-column align-center justify-center">
      <h2 class="mb-2">
        Indlæser konfiguration
      </h2>
      <v-progress-linear
        height="12"
        indeterminate
        style="max-width: 100px;"
        rounded
        rounded-bar
      />
    </v-container>
    <v-empty-state
      v-else-if="!healthy"
      title="Ingen forbindelse"
      subtitle="Vi beklager ulejligheden"
      text="Det ser ud til at der enten er noget galt med din forbindelse eller vores servere. Tjek din internetforbindelse og prøv igen."
      icon="mdi-connection"
      class="h-screen d-flex align-center"
      action-text="Prøv igen"
      @click:action="router.go(0)"
    />
    <v-empty-state
      v-else-if="!configStore.config"
      title="Forhandler ikke fundet"
      :text="`Der blev ikke fundet en forhandler. Er du sikker på adressen er rigtig?`"
      icon="mdi-magnify-remove-outline"
      class="h-screen d-flex align-center"
    />
    <v-app v-else-if="configStore.config">
      <RouterView />
    </v-app>
    <v-empty-state
      v-else
      title="En ukendt fejl skete"
      text="Noget gik galt og vi er ikke helt sikre på hvad det er."
      class="h-screen d-flex align-center"
    />
  </v-fade-transition>

  <v-snackbar-queue v-model="messages" location="center" closable close-text="Ok"></v-snackbar-queue>
</template>
